/**
 * Aritmética da proposta composta — ESPELHO de
 * sistema-faturamento-backend/src/procurement/utils/proposal-money.util.ts.
 * Mudou lá → mudar aqui (o PDF e o motor de adequação usam a versão do back;
 * esta calcula os mesmos totais na UI pra nunca divergirem).
 *
 * Regra: nunca encadear float — unitários viram inteiros ×10⁴ (4 casas, escala
 * dos portais) e totais viram centavos ×10² antes de somar.
 */

export type ChargeType = 'recorrente_mensal' | 'taxa_unica' | 'aquisicao';

export const CHARGE_TYPE_LABELS: Record<ChargeType, string> = {
  recorrente_mensal: 'Recorrente mensal',
  taxa_unica: 'Taxa única',
  aquisicao: 'Aquisição',
};

export interface ChargeableItemLike {
  quantity: number;
  unitPrice: number | string;
  chargeType?: ChargeType | string;
}

const toTenthMil = (v: number | string): number =>
  Math.round((parseFloat(String(v)) || 0) * 10000);

/**
 * Total de um item EM CENTAVOS:
 * - recorrente_mensal: mensal = round2(unit×qtd); total = mensal×meses
 * - taxa_unica:        total = round2(unit) — qtd ignorada
 * - aquisicao/legado (sem chargeType): total = round2(unit×qtd)
 */
export function computeItemTotalCents(
  item: ChargeableItemLike,
  contractMonths?: number | null,
): { mensalCents: number | null; totalCents: number } {
  const unit4 = toTenthMil(item.unitPrice);
  const qty = Number(item.quantity) || 0;
  switch (item.chargeType) {
    case 'recorrente_mensal': {
      const mensalCents = Math.round((unit4 * qty) / 100);
      const meses = contractMonths || 12;
      return { mensalCents, totalCents: mensalCents * meses };
    }
    case 'taxa_unica':
      return { mensalCents: null, totalCents: Math.round(unit4 / 100) };
    default:
      return { mensalCents: null, totalCents: Math.round((unit4 * qty) / 100) };
  }
}

/** Idem, em reais. */
export function computeItemTotal(
  item: ChargeableItemLike,
  contractMonths?: number | null,
): { mensal: number | null; total: number } {
  const { mensalCents, totalCents } = computeItemTotalCents(item, contractMonths);
  return {
    mensal: mensalCents === null ? null : mensalCents / 100,
    total: totalCents / 100,
  };
}

/** Total da proposta em reais (soma em centavos, converte no fim). */
export function computeProposalTotal(
  items: ChargeableItemLike[],
  contractMonths?: number | null,
): number {
  const cents = (items || []).reduce(
    (acc, it) => acc + computeItemTotalCents(it, contractMonths).totalCents,
    0,
  );
  return cents / 100;
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIOR DESCONTO (critério de julgamento do ITEM = "2" no ComprasNet)
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Há pregão em que o item não é disputado por PREÇO e sim por DESCONTO: o
 * campo do portal vira "Desconto (%)" e o preço unitário é DERIVADO do valor
 * estimado. Quem decide é o ITEM, não a compra — o critério da compra pode ser
 * "7" (MENOR_PRECO_OU_MAIOR_DESCONTO), misto, com os dois tipos na mesma tela.
 *
 * ⛔ HTTP 422 no PE 90009/2026 do TRT-8 (13/09/2026): o robô mandou o preço
 * estimado em reais num campo que o portal lia como percentual.
 *
 * Convenção (idêntica à do backend, e à do próprio portal): `unitPrice` é
 * SEMPRE o preço unitário em R$ — o derivado, que alimenta total, PDF e piso
 * de disputa — e `discountPercent` guarda o número que vai para o ComprasNet.
 *
 * Espelho de proposal-money.util.ts. Mudou lá → mudar aqui.
 */
export type PriceMode = 'unitario' | 'desconto';

/** Teto do percentual. O portal zera qualquer coisa acima disto. */
export const MAX_DISCOUNT_PERCENT = 100;

/** O portal aceita 2 casas no campo de desconto (maxlength 5 → "99,99"). */
export function roundDiscount(d: number): number {
  return Math.round((Number(d) || 0) * 100) / 100;
}

export function isDiscountItem(item: { priceMode?: PriceMode | string }): boolean {
  return item?.priceMode === 'desconto';
}

/**
 * Preço unitário (R$, 4 casas) que o portal calcula a partir do desconto:
 *     valorUnitario = valorEstimadoUnitario − valorEstimadoUnitario × d/100
 * Sem valor estimado não há base (item de orçamento sigiloso julgado por
 * desconto não tem preço derivável) — devolve 0.
 */
export function unitPriceFromDiscount(
  estimatedUnitPrice: number | string | null | undefined,
  discountPercent: number | string | null | undefined,
): number {
  const est4 = toTenthMil(estimatedUnitPrice ?? 0);
  if (est4 <= 0) return 0;
  const d = roundDiscount(parseFloat(String(discountPercent ?? '')) || 0);
  if (d < 0 || d > MAX_DISCOUNT_PERCENT) return 0; // fora da faixa: o portal também zera
  const bp = Math.round(d * 100); // pontos-base: 12,34% → 1234
  return Math.round((est4 * (10000 - bp)) / 10000) / 10000;
}

/**
 * Inversa: qual desconto produz este preço unitário. É o que deixa o operador
 * raciocinar em R$ (que é como a IPCOM forma preço) num campo que o portal
 * pede em percentual.
 *
 * O resultado é arredondado a 2 casas porque é só isso que o portal aceita —
 * então a volta não devolve exatamente o R$ digitado. Quem quantiza é o
 * portal; por isso a tela mostra o derivado de volta.
 */
export function discountFromUnitPrice(
  estimatedUnitPrice: number | string | null | undefined,
  unitPrice: number | string | null | undefined,
): number {
  const est4 = toTenthMil(estimatedUnitPrice ?? 0);
  if (est4 <= 0) return 0;
  const unit4 = toTenthMil(unitPrice ?? 0);
  const bp = Math.round(((est4 - unit4) * 10000) / est4);
  const clamped = Math.min(MAX_DISCOUNT_PERCENT * 100, Math.max(0, bp));
  return clamped / 100;
}
